import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag, Bookmark, ArrowRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import RewardsProgress from "./RewardsProgress";

const CartDrawer = () => {
  const {
    items, drawerOpen, setDrawerOpen, removeItem, updateQty, subtotal,
    savedItems, saveForLater, moveSavedToCart, removeSaved,
  } = useCart();
  const navigate = useNavigate();
  const [savedOpen, setSavedOpen] = useState(true);

  const renderEmptyState = (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="empty-cart">
      <div className="w-14 h-14 rounded-full bg-blush flex items-center justify-center mb-4">
        <ShoppingBag className="text-rose" />
      </div>
      <p className="font-cormorant text-2xl mb-2">Your bag is waiting</p>
      <p className="font-body text-sm text-mauve mb-6">Add pieces you love. We'll wrap them with care.</p>
      <button className="btn-primary" onClick={() => { setDrawerOpen(false); navigate("/shop"); }} data-testid="empty-cart-shop-button">
        Start shopping
      </button>
      {savedItems.length > 0 && (
        <div className="w-full mt-8">
          <SavedSection
            savedItems={savedItems}
            savedOpen={savedOpen}
            setSavedOpen={setSavedOpen}
            moveSavedToCart={moveSavedToCart}
            removeSaved={removeSaved}
            closeDrawer={() => setDrawerOpen(false)}
            showTitle
          />
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="w-[92vw] max-w-[440px] sm:max-w-[460px] bg-cream p-0 border-l border-border flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-hero text-2xl text-charcoal flex items-center justify-between">
            Your bag
            <span className="font-label text-xs text-mauve tracking-[0.2em] uppercase">
              {items.length} {items.length === 1 ? "item" : "items"}
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? renderEmptyState : (
          <>
            {/* Rewards progress bar with gift picker */}
            <RewardsProgress />

            {/* Items list + saved-for-later */}
            <div className="flex-1 overflow-y-auto">
              <AnimatePresence initial={false}>
                {items.map((it) => (
                  <motion.div
                    key={`${it.product_id}-${it.size || "_"}`}
                    layout
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 80 }}
                    transition={{ duration: 0.25 }}
                    className="flex gap-4 p-5 border-b border-border"
                    data-testid={`cart-item-${it.product_id}`}
                  >
                    <Link
                      to={`/product/${it.slug}`} onClick={() => setDrawerOpen(false)}
                      className="w-[72px] h-[88px] overflow-hidden rounded-lg bg-blush/40 flex-shrink-0"
                    >
                      {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : null}
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/product/${it.slug}`} onClick={() => setDrawerOpen(false)}
                        className="font-cormorant text-lg text-charcoal block truncate hover:text-rose"
                      >
                        {it.name}
                      </Link>
                      {it.size && (
                        <p className="font-label text-[11px] uppercase tracking-[0.2em] text-mauve">Size {it.size}</p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <div className="inline-flex items-center border border-border rounded-full">
                          <button className="p-1.5" onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)} aria-label="minus"><Minus size={14} /></button>
                          <span className="w-7 text-center font-label text-sm" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                          <button className="p-1.5" onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)} aria-label="plus"><Plus size={14} /></button>
                        </div>
                        <p className="font-cormorant text-lg">{formatINR(it.price * it.quantity)}</p>
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <button
                          onClick={() => saveForLater(it.product_id, it.size)}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-mauve hover:text-rose font-label transition-colors"
                          data-testid={`save-for-later-${it.product_id}`}
                        >
                          <Bookmark size={12} /> Save for later
                        </button>
                        <button
                          onClick={() => removeItem(it.product_id, it.size)}
                          className="text-[11px] uppercase tracking-[0.2em] text-mauve hover:text-rose font-label transition-colors"
                          data-testid={`remove-cart-${it.product_id}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {savedItems.length > 0 && (
                <SavedSection
                  savedItems={savedItems}
                  savedOpen={savedOpen}
                  setSavedOpen={setSavedOpen}
                  moveSavedToCart={moveSavedToCart}
                  removeSaved={removeSaved}
                  closeDrawer={() => setDrawerOpen(false)}
                />
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-border bg-cream p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="font-label text-[12px] uppercase tracking-[0.2em] text-mauve">Subtotal</span>
                <span className="font-hero text-2xl text-charcoal" data-testid="cart-subtotal">{formatINR(subtotal)}</span>
              </div>
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="btn-primary w-full"
                onClick={() => { setDrawerOpen(false); navigate("/checkout"); }}
                data-testid="cart-checkout-button"
              >
                Checkout →
              </motion.button>
              <button
                className="btn-link w-full justify-center mt-3"
                onClick={() => { setDrawerOpen(false); navigate("/cart"); }}
              >
                View full bag
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const SavedSection = ({ savedItems, savedOpen, setSavedOpen, moveSavedToCart, removeSaved, closeDrawer, showTitle }) => (
  <div className="bg-[#FAF4EF] border-t border-border" data-testid="saved-for-later">
    <button
      onClick={() => setSavedOpen((v) => !v)}
      className="w-full flex items-center justify-between p-5 group"
      data-testid="saved-for-later-toggle"
    >
      <div className="flex items-center gap-2">
        <Bookmark size={14} className="text-rose" />
        <span className="font-label text-[12px] uppercase tracking-[0.22em] text-charcoal">
          {showTitle ? "Still thinking about" : "Saved for later"} · {savedItems.length}
        </span>
      </div>
      <motion.span animate={{ rotate: savedOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="text-mauve">
        <ChevronDown size={16} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {savedOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          {savedItems.map((it) => (
            <div
              key={`saved-${it.product_id}-${it.size || "_"}`}
              className="flex gap-4 p-5 border-t border-border/60"
              data-testid={`saved-item-${it.product_id}`}
            >
              <Link
                to={`/product/${it.slug}`} onClick={closeDrawer}
                className="w-[60px] h-[72px] overflow-hidden rounded-lg bg-blush/40 flex-shrink-0"
              >
                {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/product/${it.slug}`} onClick={closeDrawer}
                  className="font-cormorant text-base text-charcoal block truncate hover:text-rose"
                >
                  {it.name}
                </Link>
                {it.size && (
                  <p className="font-label text-[10px] uppercase tracking-[0.2em] text-mauve mt-0.5">Size {it.size}</p>
                )}
                <p className="font-body text-sm text-charcoal mt-1">{formatINR(it.price)}</p>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => moveSavedToCart(it.product_id, it.size)}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-rose hover:text-charcoal font-label font-medium transition-colors"
                    data-testid={`move-to-cart-${it.product_id}`}
                  >
                    Move to bag <ArrowRight size={11} />
                  </button>
                  <button
                    onClick={() => removeSaved(it.product_id, it.size)}
                    className="text-[11px] uppercase tracking-[0.2em] text-mauve hover:text-rose font-label transition-colors"
                    data-testid={`remove-saved-${it.product_id}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default CartDrawer;
